import { ActionCreator } from 'redux';
import { Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';
import logger from 'lib/logger';
import { PerksActionType } from './perksActionTypes';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

interface IBaseReducerAction {
    type: PerksActionType;
}
interface IUnknownAction extends IBaseReducerAction {
    type: PerksActionType.UNKNOWN;
}
interface IProgressAction extends IBaseReducerAction {
    type: PerksActionType.START_REQUEST | PerksActionType.START_DELETE_REQUEST;
}
interface ILoadingAction extends IBaseReducerAction {
    type: PerksActionType.LOAD_SUCCESS | PerksActionType.LOAD_ERROR;
    perks?: Perk[];
    nextKey?: string;
    pages?: (string | undefined)[];
    endOfData?: boolean;
}
interface ILoadAllAction extends IBaseReducerAction {
    type: PerksActionType.LOAD_ALL_SUCCESS;
    perks?: Perk[];
    perkIdToTitle?: Map<string, string>;
}
interface ICreateAction extends IBaseReducerAction {
    type: PerksActionType.CREATE_SUCCESS;
    perk: Perk;
}
interface ICreateClubAction extends IBaseReducerAction {
    type: PerksActionType.CREATE_SUCCESS_CLUB;
    perk: Perk;
}
interface IUpdateAction extends IBaseReducerAction {
    type: PerksActionType.UPDATE_SUCCESS;
    perk: Perk;
}
interface IModalAction extends IBaseReducerAction {
    type:
        | PerksActionType.TOGGLE_CREATE_MODAL
        | PerksActionType.TOGGLE_CLOSE_CREATE_MODAL
        | PerksActionType.CLOSE_MODALS
        | PerksActionType.TOGGLE_CLOSE_UPDATE_MODAL;
}
interface IItemModalAction extends IBaseReducerAction {
    type:
        | PerksActionType.SET_ITEM_TO_DELETE
        | PerksActionType.SET_OPEN_ITEM
        | PerksActionType.SET_ITEM_TO_RESET
        | PerksActionType.SET_ITEM_TO_DUPLICATE;
    index: number;
}

interface IResetAction extends IBaseReducerAction {
    type: PerksActionType.RESET_SUCCESS | PerksActionType.RESET_ERROR;
}

interface IDeleteAction extends IBaseReducerAction {
    type: PerksActionType.DELETE_ERROR | PerksActionType.DELETE_SUCCESS;
}

interface ISetPerkAwardCountAction extends IBaseReducerAction {
    type: PerksActionType.SET_PERK_AWARD_COUNT;
    count: number;
}

interface ISetSelectRowAction extends IBaseReducerAction {
    type: PerksActionType.SET_SELECTED_ROW;
    selectedRow: GridRowSelectionModel;
}

export type IPerksAction =
    | IUnknownAction
    | IProgressAction
    | ILoadingAction
    | ICreateAction
    | ICreateClubAction
    | IUpdateAction
    | IModalAction
    | IItemModalAction
    | IResetAction
    | IDeleteAction
    | ILoadAllAction
    | ISetPerkAwardCountAction
    | ISetSelectRowAction;

export function getAllPerks() {
    return async function (dispatch: ActionCreator<ILoadingAction | IProgressAction>) {
        dispatch({ type: PerksActionType.START_REQUEST });
        try {
            const pepperPerksResponse = await perkApi.getList();

            if (!pepperPerksResponse.ok) {
                throw new Error(`Failed to load perks with code: ${pepperPerksResponse.statusCode}`);
            }
            // eslint-disable-next-line prefer-destructuring
            const perks: Perk[] = pepperPerksResponse.body.perks;
            const perksMap = new Map<string, string>();
            if (Array.isArray(perks)) {
                perks.forEach(perk => {
                    const { _id, title, internalTitle } = perk ?? {};
                    let finalTitle = '';
                    if ((title ?? '').trim()) {
                        if (internalTitle) {
                            finalTitle = `${internalTitle} - ${title}`;
                        } else {
                            finalTitle = title;
                        }
                    } else if (internalTitle) {
                        finalTitle = internalTitle;
                    }
                    perksMap.set(_id, finalTitle);
                });
            }

            dispatch({
                perkIdToTitle: perksMap,
                perks,
                type: PerksActionType.LOAD_ALL_SUCCESS
            });
        } catch (e) {
            logger.error('Failed to load all perks', e);
            dispatch({
                type: PerksActionType.LOAD_ERROR
            });
        }
    };
}
